import Elevio from 'elevio/lib/client'

export default defineNuxtPlugin((nuxtApp) => {
  const ELEVIO_DISMISS_KEY = 'elevio:autoDismissed:session:v3'
  const LEGACY_DISMISS_KEYS = [
    'elevio:autoDismissed:session',
    'elevio:autoDismissed:session:v2',
  ]
  let closeHandlerBound = false
  let pendingOpenTarget = null
  let manualLauncherOpenRequested = false

  const clearLegacyDismissKeys = () => {
    if (typeof sessionStorage === 'undefined') return
    LEGACY_DISMISS_KEYS.forEach((key) => {
      sessionStorage.removeItem(key)
    })
  }

  const isAutoDismissedForSession = () => {
    if (typeof sessionStorage === 'undefined') return false
    return sessionStorage.getItem(ELEVIO_DISMISS_KEY) === '1'
  }

  const setAutoDismissedForSession = (dismissed = true) => {
    if (typeof sessionStorage === 'undefined') return
    if (dismissed) {
      sessionStorage.setItem(ELEVIO_DISMISS_KEY, '1')
    } else {
      sessionStorage.removeItem(ELEVIO_DISMISS_KEY)
    }
  }

  const bindDismissHandlers = () => {
    if (typeof document === 'undefined') return
    if (closeHandlerBound) return
    closeHandlerBound = true
    document.addEventListener(
      'click',
      (event) => {
        if (event?.target?.closest?.('._elevio_launcher')) {
          manualLauncherOpenRequested = true
        }

        if (event?.target?.closest?.('._elevio_close')) {
          manualLauncherOpenRequested = false
          setAutoDismissedForSession(true)
        }
      },
      true
    )

    Elevio.on('widget:closed', () => {
      manualLauncherOpenRequested = false
    })

    // If auto-popups are suppressed, opening via launcher should show
    // the article associated with the current page rather than stale content.
    Elevio.on('widget:opened', () => {
      if (!manualLauncherOpenRequested) return
      manualLauncherOpenRequested = false

      if (!pendingOpenTarget) return

      if (pendingOpenTarget.type === 'article') {
        Elevio.openArticle(pendingOpenTarget.articleId)
      } else if (pendingOpenTarget.type === 'home') {
        Elevio.openHome()
      }

      pendingOpenTarget = null
    })
  }

  const onWidgetOpened = async () => {
    try {
      // 1️⃣ fetch a fresh JWT from backend
      const { data: kbToken } = await nuxtApp.$axios.get(`/sso/kb/token`)

      // 2️⃣ create hidden <img> dynamically
      const img = new Image()
      img.src = `${nuxtApp.$config.public.knowledgeCenterBaseUrl}/sso/check?jwt=${kbToken}`
      img.style.display = 'none'

      // Optional: handle load/error
      img.onload = () => console.log('Foreign cookie request sent!')
      img.onerror = (e) => {
        console.warn('Foreign cookie request failed', e)
        error.value = e
      }

      // 3️⃣ append to DOM
      document.body.appendChild(img)

      // Optional: remove after load to avoid DOM clutter
      img.onload = img.onerror = () => img.remove()
    } catch (e) {
      console.error('Failed to prefetch foreign cookie', e)
      error.value = e
    }
  }

  const initElevio = async (userInfo = false) => {
    try {
      clearLegacyDismissKeys()
      const user = userInfo || (await nuxtApp.$axios.get(`/me`))
      const userHash = await nuxtApp.$axios.get(`/sso/kb/userHash`)
      if (user && user.data && userHash && userHash.data) {
        await Elevio.on('load', function (_elev) {
          const elevioUser = {
            email: user.data.email,
            first_name: user.data.first_name,
            last_name: user.data.last_name,
            groups: user.data.group,
            user_hash: userHash.data,
            registered_at: user.data.created_at,
          }
          _elev.setUser(elevioUser)
        })
        await Elevio.on('load', function (_elev) {
          _elev.setSettings({
            pushin: window.visualViewport.width >= 1024,
            disableDevelopmentWarnings: true,
          })
        })
        await Elevio.on('widget:opened', async function (_elev) {
          await onWidgetOpened()
        })
      }
    } catch (e) {
      nuxtApp.$toast.error(e)
    }

    await Elevio.load(nuxtApp.$config.public.elevioClientId)
    bindDismissHandlers()
  }
  const initElevioBeforeLogin = async () => {
    try {
      clearLegacyDismissKeys()
      await Elevio.on('load', function (_elev) {
        _elev.setSettings({
          pushin: window.visualViewport.width >= 1024,
          disableDevelopmentWarnings: true,
        })
      })
    } catch (e) {
      nuxtApp.$toast.error(e)
    }
    await Elevio.load(nuxtApp.$config.public.elevioClientId)
    bindDismissHandlers()
  }
  const logoutElevio = () => {
    Elevio.logoutUser()
  }
  const close = () => {
    window.visualViewport.width >= 768 && Elevio.close()
  }
  const openArticle = (articleId) => {
    if (isAutoDismissedForSession()) {
      pendingOpenTarget = {
        type: 'article',
        articleId,
      }
      Elevio.close()
      return
    }
    pendingOpenTarget = null
    setTimeout(() => {
      if (
        window.visualViewport.width >= 720 ||
        window.visualViewport.width + 14 >= 720
      ) {
        Elevio.openArticle(articleId)
      } else {
        Elevio.on('widget:opened', () => {
          Elevio.openArticle(articleId)
        })
      }
    }, 1250)
  }
  const openHome = () => {
    if (isAutoDismissedForSession()) {
      pendingOpenTarget = {
        type: 'home',
      }
      Elevio.close()
      return
    }
    pendingOpenTarget = null
    Elevio.openHome()
  }
  const on = (type, callback) => {
    window.visualViewport.width >= 768 && Elevio.on(type, callback)
  }

  return {
    provide: {
      nuxtElevio: {
        initElevio,
        initElevioBeforeLogin,
        logoutElevio,
        close,
        openArticle,
        openHome,
        on,
        onWidgetOpened,
      },
    },
  }
})
